import { AlertCircle, Building2, Check } from 'lucide-react'
import { format } from 'date-fns'
import { Controller } from 'react-hook-form'
import { parseApiTimestampToDate } from '@/lib/utils/parseApiTimestamp'
import { formatPhoneNumber, cleanPhoneNumber } from '@/features/invitee/utils/inviteeFormatters'
import type { UseCompanyInfoTabReturn } from '@/features/company/types'
import styles from '../../../styles/companyAdd.module.css'
import { PHONE_FORMAT_ERROR, isValidPhoneForSave } from '@/features/company/utils/companyInfoTabLogic'
import { formatPafSummaryDate } from '@/features/company/utils/pafDateFormat'
import { SectionCard } from '@/components/ui/SectionCard'
import { cn } from '@lib/utils/helpers'

function StatusHeaderToggle({
    watch,
    setValue,
    canEditListed,
    setShowDisableConfirm,
}: {
    watch: UseCompanyInfoTabReturn['watch']
    setValue: UseCompanyInfoTabReturn['setValue']
    canEditListed: boolean
    setShowDisableConfirm: (show: boolean) => void
}) {
    const listed = watch('listed') ?? '1'
    const isActive = String(listed) === '1'

    const handleToggle = (targetValue: '1' | '0') => {
        if (!canEditListed || String(listed) === targetValue) return
        if (targetValue === '0') {
            setValue('listed', '0', { shouldDirty: true })
            setShowDisableConfirm(true)
        } else {
            setValue('listed', '1', { shouldDirty: true })
        }
    }

    return (
        <div className="flex items-center gap-2" data-testid="company-info-status-header-container">
            <span className="text-sm font-bold text-slate-800">Status</span>
            <div
                className={cn(
                    'inline-flex items-center rounded-lg bg-slate-100 p-1 border border-slate-200/80 text-xs font-medium',
                    !canEditListed && 'opacity-60 cursor-not-allowed'
                )}
                data-testid="company-info-status-toggle-group"
            >
                <button
                    type="button"
                    disabled={!canEditListed}
                    onClick={() => handleToggle('1')}
                    data-testid="company-info-status-active-toggle"
                    className={cn(
                        'inline-flex items-center gap-1.5 rounded-md px-3.5 py-1.5 text-xs font-medium transition-all duration-150',
                        isActive
                            ? 'bg-gradient-to-b from-[rgb(44_160_255)] to-[rgb(12_128_220)] text-white font-semibold border border-[#0c80dc]/30 shadow-sm shadow-sky-500/20'
                            : 'bg-white text-slate-700 border border-slate-200/80 hover:bg-slate-50'
                    )}
                >
                    {isActive && <Check className="h-3.5 w-3.5" />}
                    <span>Active</span>
                </button>
                <button
                    type="button"
                    disabled={!canEditListed}
                    onClick={() => handleToggle('0')}
                    data-testid="company-info-status-inactive-toggle"
                    className={cn(
                        'inline-flex items-center gap-1.5 rounded-md px-3.5 py-1.5 text-xs font-medium transition-all duration-150',
                        !isActive
                            ? 'bg-gradient-to-b from-[rgb(44_160_255)] to-[rgb(12_128_220)] text-white font-semibold border border-[#0c80dc]/30 shadow-sm shadow-sky-500/20'
                            : 'bg-white text-slate-700 border border-slate-200/80 hover:bg-slate-50'
                    )}
                >
                    {!isActive && <Check className="h-3.5 w-3.5" />}
                    <span>Not active</span>
                </button>
            </div>
        </div>
    )
}

export function CompanyInfoCompanySection({
        company,
        register,
        control,
        errors,
        clearErrors,
        setError,
        canEditInfo,
        canEditListed,
        isLibertyRole,
        watch,
        setValue,
        setShowDisableConfirm,
}: UseCompanyInfoTabReturn) {
    return (
            <SectionCard
                icon={Building2}
                title="Company Information"
                testId="company-info-section-company-information"
                headerActions={
                    <StatusHeaderToggle
                        watch={watch}
                        setValue={setValue}
                        canEditListed={canEditListed}
                        setShowDisableConfirm={setShowDisableConfirm}
                    />
                }
            >
                <div className="space-y-5">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className={styles.formGroup}>
                            <label className={styles.formLabel}>
                                Company Name<span className={styles.requiredStar}>*</span>
                            </label>
                            <input
                                type="text"
                                disabled={!canEditInfo}
                                {...register("company_name", { required: "Company Name is required" })}
                                data-testid="company-info-company-name-input"
                                className={`${styles.customFormControl} ${errors.company_name ? '!border-red-500' : ''}`}
                            />
                            {errors.company_name && (
                                <div className={styles.errorMessage}>
                                    <AlertCircle className="h-4 w-4" /> {errors.company_name.message as string}
                                </div>
                            )}
                        </div>
                        <div className="flex gap-3 items-start">
                            <div className={`${styles.formGroup} flex-1`}>
                                <label className={styles.formLabel}>
                                    Legal Name<span className={styles.requiredStar}>*</span>
                                </label>
                                <input
                                    type="text"
                                    disabled={!canEditInfo}
                                    {...register("legal_name", { required: "Legal Name is required" })}
                                    data-testid="company-info-legal-name-input"
                                    className={`${styles.customFormControl} ${errors.legal_name ? '!border-red-500' : ''}`}
                                />
                                {errors.legal_name && (
                                    <div className={styles.errorMessage}>
                                        <AlertCircle className="h-4 w-4" /> {errors.legal_name.message as string}
                                    </div>
                                )}
                                {company?.verified_by_name && company?.verified_date && (
                                    <p
                                        data-testid="company-info-legal-name-verified-meta"
                                        className="mt-1.5 text-[13px] text-[#778aa4]"
                                    >
                                        {company.verified_by_name} — {formatPafSummaryDate(company.verified_date)}
                                    </p>
                                )}
                            </div>
                            <div className={styles.formGroup} style={{ width: "90px", flexShrink: 0 }}>
                                <label className={styles.formLabel}>Verified</label>
                                <div className={styles.selectWrap}>
                                    <select
                                        disabled={!canEditInfo}
                                        className={styles.customFormControl}
                                        {...register("legal_verified")}
                                        data-testid="company-info-legal-verified-select"
                                    >
                                        <option value="">Select</option>
                                        <option value="YES">Yes</option>
                                        <option value="NO">No</option>
                                    </select>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div
                        className={`grid grid-cols-1 gap-6 ${isLibertyRole ? "md:grid-cols-2" : ""}`}
                    >
                        <div className={styles.formGroup}>
                            <label className={styles.formLabel}>Office Phone</label>
                            <Controller
                                name="office_phone"
                                control={control}
                                rules={{
                                    validate: (value) =>
                                        isValidPhoneForSave(value, { required: false }) || PHONE_FORMAT_ERROR,
                                }}
                                render={({ field }) => (
                                    <input
                                        type="text"
                                        disabled={!canEditInfo}
                                        placeholder="(xxx) xxx-xxxx"
                                        {...field}
                                        value={field.value ?? ""}
                                        onChange={(e) => {
                                            const cleaned = cleanPhoneNumber(e.target.value)
                                            const formatted = formatPhoneNumber(cleaned)
                                            field.onChange(formatted)
                                            if (isValidPhoneForSave(formatted, { required: false })) {
                                                clearErrors("office_phone")
                                            } else {
                                                setError("office_phone", { type: "manual", message: PHONE_FORMAT_ERROR })
                                            }
                                        }}
                                        data-testid="company-info-office-phone-input"
                                        className={`${styles.customFormControl} ${errors.office_phone ? '!border-red-500' : ''}`}
                                    />
                                )}
                            />
                            {errors.office_phone && (
                                <div className={styles.errorMessage}>
                                    <AlertCircle className="h-4 w-4" /> {errors.office_phone.message as string}
                                </div>
                            )}
                        </div>

                        {isLibertyRole && (
                            <div className={styles.formGroup}>
                                <label className={styles.formLabel}>Company Alias Name</label>
                                <input
                                    type="text"
                                    disabled={!canEditInfo}
                                    {...register("company_alias_name")}
                                    data-testid="company-info-company-alias-name-input"
                                    className={styles.customFormControl}
                                />
                            </div>
                        )}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className={styles.formGroup}>
                            <label className={styles.formLabel}>Primary Contact</label>
                            <input
                                type="text"
                                disabled={!canEditInfo}
                                {...register("contact_name")}
                                data-testid="company-info-contact-name-input"
                                placeholder="Full name"
                                className={`${styles.customFormControl} ${errors.contact_name ? '!border-red-500' : ''}`}
                            />
                            {errors.contact_name && (
                                <p className="mt-1 text-sm text-red-600">{errors.contact_name.message}</p>
                            )}
                        </div>

                        <div className={styles.formGroup}>
                            <label className={styles.formLabel}>Primary Phone</label>
                            <Controller
                                name="contact_mobile"
                                control={control}
                                rules={{
                                    validate: (value) =>
                                        isValidPhoneForSave(value, { required: false }) || PHONE_FORMAT_ERROR,
                                }}
                                render={({ field }) => (
                                    <input
                                        type="text"
                                        disabled={!canEditInfo}
                                        placeholder="(xxx) xxx-xxxx"
                                        {...field}
                                        value={field.value ?? ""}
                                        onChange={(e) => {
                                            const cleaned = cleanPhoneNumber(e.target.value)
                                            const formatted = formatPhoneNumber(cleaned)
                                            field.onChange(formatted)
                                            if (isValidPhoneForSave(formatted, { required: false })) {
                                                clearErrors("contact_mobile")
                                            } else {
                                                setError("contact_mobile", { type: "manual", message: PHONE_FORMAT_ERROR })
                                            }
                                        }}
                                        data-testid="company-info-contact-mobile-input"
                                        className={`${styles.customFormControl} ${errors.contact_mobile ? '!border-red-500' : ''}`}
                                    />
                                )}
                            />
                            {errors.contact_mobile && (
                                <div className={styles.errorMessage}>
                                    <AlertCircle className="h-4 w-4" /> {errors.contact_mobile.message as string}
                                </div>
                            )}
                        </div>
                    </div>
                    <div data-testid="company-info-transfer-status-display" style={{ fontSize: "13px", color: "#778aa4" }}>
                        <span>Transfer Status : </span>
                        <span style={{
                            color: company?.transfer_log_status?.status === "Pending" ? "#e67e22" : "#778aa4",
                            fontWeight: company?.transfer_log_status?.status === "Pending" ? 600 : 400
                        }}>
                            {company?.transfer_log_status?.status === "Completed"
                                ? `Transferred ${(() => {
                                      const d = parseApiTimestampToDate(company?.transfer_log_status?.date)
                                      return d ? format(d, 'MM/dd/yyyy hh:mm a') : ""
                                  })()}`
                                : company?.transfer_log_status?.status === "Pending"
                                    ? "Transfer Pending"
                                    : "No Transfer History"}
                        </span>
                    </div>
                </div>
            </SectionCard>
    )
}
